import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useFilterContext } from "../../../state/FilterContext";
import { useArrayContext } from "../../../state/ArrayContext";
import { useSiteContext } from "../../../state/SiteContext";
import { SourceItem } from "../../../shared/models/source-item.model";
import { services } from "../../../globals";
import { MetadataRequest } from "../../../shared/models/metadata-request.model";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";

interface CustomPropertyValuesQueryProps {
    propertyNameArg?: string | null,
    children?: ReactNode,
}

/***
 * Array source that returns all values of a custom property from metadata on the site that match the filter.
 */
export const CustomPropertyValuesQuery = React.memo(function CustomPropertyValuesQuery({ propertyNameArg, children }: CustomPropertyValuesQueryProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const { siteState } = useSiteContext();
    const { filterState } = useFilterContext();
    const lastRequestSignatureRef = useRef<string | null>(null);

    // Get property name from the property child unless it was provided as a prop
    const [propertyName, setPropertyName] = useState("");
    const propertyChild = useMemo(() => propertyNameArg ? null : findFirstComponentOfType(children, "arg-property"), [children, propertyNameArg]);
    const usedPropertyName = propertyNameArg || propertyName;

    const requestInfo = useMemo(() => {
        if (!siteState.site?.name || !usedPropertyName) {
            return { request: null, signature: "" };
        }
        const request: MetadataRequest = { ...filterState };
        // Remove the custom property from the request to avoid it being applied to the search
        request.customProperties = { ...filterState.customProperties };
        delete request.customProperties?.[usedPropertyName];

        return {
            request,
            signature: JSON.stringify({
                site: siteState.site.name,
                propertyName: usedPropertyName,
                request,
                isValidity: false,
                isSystemProperty: false
            })
        };
    }, [siteState.site?.name, usedPropertyName, filterState]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (siteState.site?.name && usedPropertyName && requestInfo.request) {
                if (requestInfo.signature && lastRequestSignatureRef.current === requestInfo.signature) {
                    return;
                }
                lastRequestSignatureRef.current = requestInfo.signature;
                const valueItems: Array<SourceItem> = await services.search.getPropertyValueSet(
                    siteState.site?.name || "",
                    requestInfo.request,
                    usedPropertyName,
                    false,
                    false
                );
                const result = valueItems.map((item, ix) => {
                    return { ...item, position: ix + 1 };
                });
                if (isMounted) {
                    setArray(result);
                }
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, [siteState, setArray, usedPropertyName, requestInfo]);

    return <>
        {propertyChild && (
            <TextExtractor onExtractedText={setPropertyName}>
                {propertyChild?.props.children}
            </TextExtractor>
        )}
    </>;
});